import { InfoIcon } from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";

export interface FieldSchematicContent {
    label: string;
    isOptional: boolean;
    hasTooltip: boolean;
    message: string | null;
}

const defaultContent: FieldSchematicContent = {
    label: "Business email",
    isOptional: true,
    hasTooltip: true,
    message: "Business address",
};

/** X-ray plate for Field: label row, md Input boundary, and message line. */
export const FieldSchematic = ({
    bindings,
    highlight,
    content = defaultContent,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    content?: FieldSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const labelStyle = {
        color: read("label", "color"),
        fontFamily: read("label", "font-family"),
        fontSize: read("label", "font-size"),
        fontWeight: read("label", "font-weight"),
        lineHeight: read("label", "line-height"),
        letterSpacing: read("label", "letter-spacing"),
    };
    const messageStyle = {
        color: read("message", "color"),
        fontFamily: read("message", "font-family"),
        fontSize: read("message", "font-size"),
        fontWeight: read("message", "font-weight"),
        lineHeight: read("message", "line-height"),
        letterSpacing: read("message", "letter-spacing"),
    };

    return (
        <Part
            partId="background"
            highlight={highlight}
            className={styles.schematic}
            style={{
                flexDirection: "column",
                alignItems: "stretch",
                width: 240,
                gap: read("background", "gap"),
            }}
        >
            <span
                style={{
                    display: "inline-flex",
                    alignSelf: "flex-start",
                    alignItems: "center",
                    gap: read("background", "gap"),
                }}
            >
                <span
                    style={{
                        display: "inline-flex",
                        gap: read("background", "gap"),
                    }}
                >
                    <Part
                        partId="label"
                        highlight={highlight}
                        style={labelStyle}
                    >
                        {content.label}
                    </Part>
                    {content.isOptional && (
                        <Part
                            partId="optional"
                            highlight={highlight}
                            style={{
                                ...labelStyle,
                                color: read("optional", "color"),
                            }}
                        >
                            (optional)
                        </Part>
                    )}
                </span>
                {content.hasTooltip && (
                    <Part
                        partId="glyph"
                        highlight={highlight}
                        style={{
                            display: "inline-flex",
                            width: read("glyph", "width"),
                            height: read("glyph", "height"),
                            color: read("glyph", "color"),
                        }}
                    >
                        <InfoIcon />
                    </Part>
                )}
            </span>
            <Part
                partId="border"
                highlight={highlight}
                style={{
                    boxSizing: "border-box",
                    width: "100%",
                    height: read("border", "height"),
                    border: "1px solid var(--border-default)",
                    borderRadius: "var(--radius-m)",
                    background: "var(--bg-primary)",
                }}
            />
            {content.message !== null && (
                <Part
                    partId="message"
                    highlight={highlight}
                    style={messageStyle}
                >
                    {content.message}
                </Part>
            )}
        </Part>
    );
};
